<template>
  <layout>
    <template v-slot:header>
      <AppHead :title="`Product Bundle - ${product.sku}`" />
      <h2>Bundle - Product {{ product.sku }} - {{ product.name }}</h2>
      <flash-messages />
    </template>
        
    <template v-slot:default>
      <h3>Included Products</h3>
      <div class="alert alert-warning" role="alert" v-if="parentProduct.is_bundle_in_active_sale">
        <div class="">
          <i class="fa fa-exclamation-triangle"></i> This bundle has active sale. Please end the sale before changing price or quantity.
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-striped tbody-border">
          <thead>
            <tr>
              <th class="">SKU</th>
              <th class="">Name</th>
              <th class="">MTN Cost</th>
              <th class="">Normal Price</th>
              <th class="">Item Price</th>
              <th class="">Percent Discount</th>
              <th class="">Normal MSRP</th>
              <th class="">Price MSRP</th>
              <th class="">Qty</th>
              <th class="">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in included_products" :key="product.id">
              <td>{{ product.sku }}</td>
              <td>{{ product.name }}</td>
              <td>{{ formatDollars(product.mtn_cost) }}</td>
              <td>{{ formatDollars(product.wholesale_price_fixed) }}</td>
              <td>
                <div class="input-group">
                  <input :disabled="parentProduct.is_bundle_in_active_sale" type="text" class="form-control" v-model="product.item_price"/>
                </div>
              </td>
              <td>{{ formatCurrency(bundleItemPercentDiscount(product)) }}% </td>
              <td>{{ formatCurrency(product.wholesale_msrp) }} </td>
              <td>
                <div class="input-group">
                  <input type="text" class="form-control" v-model="product.price_msrp"/>
                </div>
              </td>
              <td>
              <div class="input-group">
                <input :disabled="parentProduct.is_bundle_in_active_sale" type="text" class="form-control" v-model="product.qty"/>
                <div class="input-group-append">
                  <span class="input-group-text">
                    <i @click="removeProduct(product)" class="fas fa-trash icon-hover text-gray fa-lg float-right"></i>
                  </span>
                </div>
              </div>
              </td>
              <td>
                <input type="text" class="form-control" v-model="product.description"/>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td>
              <td></td>
              <td>{{ formatDollars(total_mtn_cost) }}</td>
              <td>{{ formatDollars(total_wholesale) }}</td>
              <td>{{ formatDollars(wholesalePriceFixed) }}</td>
              <td></td>
              <td>{{ formatDollars(total_price_msrp) }}</td>
              <td>{{ formatDollars(msrp) }}</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td colspan="9">
                <jet-button
                  class="float-right"
                  style="width:100px"
                  :disabled="updateForm.processing || !updateFormHasChanged"
                  :loading="updateForm.processing"
                  @click="updateBundleProductItem"
                >Save</jet-button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <section class="mt-3" v-if="!parentProduct.is_bundle_in_active_sale">
        <h3>Add Product</h3>

        <Search :searchText="searchText" @searched="search"/>

        <DataTable ref="admin-products-lookup"
        :value="products" :paginator="true"
        :lazy="true"
        :first="(pagination.page - 1) * pagination.per_page"
        :rows="pagination.per_page"
        :totalRecords="pagination.total"
        @page="onPage($event)"
        :sortField="sortField"
        :sortOrder="sortOrder"
        @sort="onSort"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" currentPageReportTemplate="({first} - {last} of {totalRecords})"
        paginatorPosition="both" removableSort
        :rowsPerPageOptions="[10, 20, 50, 100]"
        class="p-datatable-sm admin-datatable"
        tableClass="border-left border-right border-bottom"
        stateStorage="local" stateKey="admin-products-lookup" dataKey="id"
        @row-click="(e) => includeProduct(e.data.id)">
            <template #paginatorLeft>
              {{ products.length }} Products
            </template>
            <Column field="name" header="Name" :sortable="true"></Column>
            <Column field="sku" header="SKU" :sortable="true"></Column>
            <Column field="price_msrp" header="MSRP" :sortable="true">
              <template #body="slotProps">
                {{ formatDollars(slotProps.data.price_msrp) }}
              </template>
            </Column>

            <Column field="inventoryInfo" header="Inventory" :sortable="true" sortField="inventoryInfo.current_inventory">
              <template #body="slotProps">
                <InventoryInfo :inventoryInfo="slotProps.data.inventoryInfo" />
              </template>
            </Column>

            <Column field="created_at" header="Created" :sortable="true">
              <template #body="slotProps">
                {{ moment(slotProps.data.created_at).format('M/D/YY h:mm A') }}
              </template>
            </Column>
            <Column field="updated_at" header="Updated" :sortable="true">
              <template #body="slotProps">
                {{ moment(slotProps.data.updated_at).format('M/D/YY h:mm A') }}
              </template>
            </Column>
        </DataTable>
      </section>

    </template>
  </layout>
</template>

<script>

import Layout from '@/Shared/Admin/Layout'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import AmountInput from '@/Shared/AmountInput'
import Search from '@/Shared/Admin/Search'
import InventoryInfo from '@/Components/General/InventoryInfo'

import moment from 'moment'

export default {
  components: {
    JetActionMessage,
    JetButton,
    JetFormSection,
    JetInput,
    JetCheckbox,
    JetInputError,
    JetLabel,
    Layout,
    Search,
    AmountInput,
    InventoryInfo,
  },
  props: {
    product: Object,
    included_products: Array,
    total_price_msrp: Number,
    total_wholesale: Number,
    total_mtn_cost: Number,
  },
  remember: 'form',
  data() {
    return {
      sortField: null, // current sort field
      sortOrder: null, // current sort order
      pagination: {
        total: 0,
        per_page: 20,
        page: 1,
      },
      parentProduct: this.product,
      searchText: null,
      products: [],
      totalPrice: 0,
      form: this.$inertia.form({
        included_product_id: null,
        qty: null,
        item_price: null,
        price_msrp: null,
        description: null,
      }),
      updateForm: this.$inertia.form({
        items: [],
      }),
    }
  },
  computed: {
    updateFormHasChanged() {
      const original = this.product.included_products.map(p => {
        return {
          included_product_id: p.pivot.included_product_id,
          qty: p.pivot.qty,
          description: p.pivot.description || '',
          item_price: p.pivot.item_price,
          price_msrp: p.pivot.price_msrp,
        }
      })
      const change = this.included_products.map(p => {
        return {
          included_product_id: p.id,
          qty: p.qty,
          description: p.description || '',
          item_price: p.item_price,
          price_msrp: p.price_msrp,
        }
      })
      return JSON.stringify(original) != JSON.stringify(change)
    },
    msrp() {
      return this.included_products.reduce((t, p) => t + p.price_msrp * p.qty, 0);
    },
    wholesalePriceFixed() {
      return this.included_products.reduce((t, p) => t + p.item_price * p.qty, 0);
    }
  },
  created() {
    this.moment = moment;
  },
  methods: {
    bundleItemPercentDiscount(product) {
      return (product.wholesale_price_fixed - product.item_price) / product.wholesale_price_fixed * 100
    },
    search(e, searchText) {
      this.searchText = searchText
      this.$refs['admin-products-lookup'].resetPage()

      this.postSearch()
    },
    onPage(e) {
      this.pagination.page = e.page + 1
      this.pagination.per_page = e.rows
      this.postSearch()
    },
    onSort(events) {
      this.sortField = events.sortField;
      this.sortOrder = events.sortOrder;
      this.postSearch();
    },
    postSearch() {
      axios.post(route('products.search'), {
        sort_field: this.sortField,
        sort_order: this.sortOrder,
        query: this.searchText || '',
        include_inventory: 1,
        filter_active: -1
      }, {
        params: {
          page: this.pagination.page, per_page: this.pagination.per_page
        }
      }).then((response) => {
        if (response.data.success) {
          this.products = response.data.data;
          this.pagination.total = response.data.total
        }
      })
    },
    includeProduct(id) {
      if (this.parentProduct.is_bundle_in_active_sale) {
        return;
      }
      this.form.included_product_id = id;
      this.form.post(route('admin.products.bundleAdd', this.product));
    },
    removeProduct(product) {
      if (this.parentProduct.is_bundle_in_active_sale) {
        return;
      }
      this.included_products.splice(this.included_products.findIndex(p => p.id == product.id), 1);
    },
    updateBundleProductItem(e) {
      if (this.parentProduct.is_bundle_in_active_sale) {
        return;
      }
      if (this.updateForm.processing) {
        return;
      }
      this.updateForm.items = this.included_products.map(p => {
        return {
          included_product_id: p.id,
          qty: p.qty,
          description: p.description,
          item_price: p.item_price,
          price_msrp: p.price_msrp,
        }
      })

      this.updateForm.post(route('admin.products.bundleUpdate', this.product));
    },
  },
}
</script>

<style>
  #file_select {
    border: 2px dashed #0087F7;
    border-radius: 5px;
    background: white;
    min-height: 150px;
    padding: 50px;
  }
</style>
